import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, 
  User, 
  Globe, 
  Users, 
  Baby, 
  MessageSquare, 
  Calendar,
  MapPin,
  Clock,
  Star,
  UserCheck
} from 'lucide-react';

const EarlyAccessRequestModal = ({ request, isOpen, onClose }) => {
  if (!isOpen || !request) return null;

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      >
        <motion.div
          className="bg-white/95 backdrop-blur-xl rounded-3xl border border-white/20 shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          onClick={e => e.stopPropagation()}
        >
          {/* Header */}
          <div className="relative p-6 border-b border-gray-200/50">
            <button
              onClick={onClose}
              className="absolute top-4 right-4 w-10 h-10 bg-gray-100/60 backdrop-blur-sm rounded-full border border-gray-200/30 flex items-center justify-center hover:bg-gray-200/60 transition-all duration-300"
            >
              <X className="w-5 h-5 text-gray-600" />
            </button>

            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-500 rounded-xl flex items-center justify-center">
                <UserCheck className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">
                  {request.first_name} {request.last_name}
                </h2>
                <p className="text-gray-600 flex items-center space-x-1">
                  <Calendar className="w-4 h-4" />
                  <span>{formatDate(request.timestamp)}</span>
                </p>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="p-6 space-y-6">
            {/* Personal Information */}
            <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-2xl p-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center space-x-2">
                <User className="w-5 h-5 text-blue-600" />
                <span>Informations Personnelles</span>
              </h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="flex items-center space-x-3">
                  <Globe className="w-4 h-4 text-gray-500" />
                  <div>
                    <p className="text-sm text-gray-500">Pays</p>
                    <p className="font-medium text-gray-900">{request.country}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <MapPin className="w-4 h-4 text-gray-500" />
                  <div>
                    <p className="text-sm text-gray-500">Localisation</p>
                    <p className="font-medium text-gray-900">
                      {request.city || 'Non disponible'}, {request.country_code || 'XX'}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Family Information */}
            <div className="bg-gradient-to-r from-green-50 to-teal-50 rounded-2xl p-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center space-x-2">
                <Users className="w-5 h-5 text-green-600" />
                <span>Informations Familiales</span>
              </h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="flex items-center space-x-3">
                  <Users className="w-4 h-4 text-gray-500" />
                  <div>
                    <p className="text-sm text-gray-500">Nombre d'enfants</p>
                    <p className="font-medium text-gray-900">
                      {request.number_of_children}
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <Baby className="w-4 h-4 text-gray-500" />
                  <div>
                    <p className="text-sm text-gray-500">Petits-enfants</p>
                    <p className="font-medium text-gray-900">
                      {request.number_of_grandchildren}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Message */}
            <div className="bg-gradient-to-r from-orange-50 to-yellow-50 rounded-2xl p-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center space-x-2">
                <MessageSquare className="w-5 h-5 text-orange-600" />
                <span>Message</span>
              </h3>
              <div className="bg-white/60 rounded-xl p-4 border border-orange-200/50">
                <p className="text-gray-700 leading-relaxed">
                  "{request.message}"
                </p>
              </div>
            </div>

            {/* Technical Information */}
            <div className="bg-gradient-to-r from-gray-50 to-slate-50 rounded-2xl p-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center space-x-2">
                <Clock className="w-5 h-5 text-gray-600" />
                <span>Informations Techniques</span>
              </h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">IP Address:</span>
                  <span className="font-mono text-gray-700">
                    {request.ip_address ? `${request.ip_address.substring(0, 8)}***` : 'Non disponible'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">User Agent:</span>
                  <span className="font-mono text-gray-700 truncate max-w-xs">
                    {request.user_agent ? request.user_agent.substring(0, 50) + '...' : 'Non disponible'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Email Sent:</span>
                  <span className={`font-medium ${request.email_sent ? 'text-green-600' : 'text-red-600'}`}>
                    {request.email_sent ? '✅ Oui' : '❌ Non'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default EarlyAccessRequestModal;